// 급여 관련 계산 유틸리티 함수들
import { Employee, TeamIncentiveCalculation, BudgetData } from '../contexts/AppContext';
import { defaultTaxTable } from '../data/taxTable.ts';

// 인센티브 비율 (성과/팀장/팀원 배분)
export interface IncentiveRatio {
  performance: number;
  team: number;
  member: number;
}

/**
 * 팀별 인센티브 비율 해석 (월별 우선 → 전역 동결 폴백)
 *
 * 폴백 순서:
 *   1) monthlyData[month].teamIncentiveRatios[team]  // 월별 팀 비율 (월별화 이후의 소스)
 *   2) budgetData.teamIncentiveRatios[team]          // 전역 팀 비율 (동결 레거시)
 *   3) budgetData.incentiveRatios                    // 전역 기본 비율 (동결)
 *   4) { performance:10, team:70, member:30 }        // 하드코드 기본
 *
 * 월별 데이터가 없는 기존 월은 2)~3)으로 폴백되어 월별화 이전과 동일하게 동작한다(하위호환).
 */
export const resolveTeamIncentiveRatio = (
  budgetData: BudgetData | undefined,
  month: string | undefined,
  teamName: string
): IncentiveRatio => {
  const monthly = month
    ? budgetData?.monthlyData?.[month]?.teamIncentiveRatios?.[teamName]
    : undefined;
  return (
    monthly ||
    budgetData?.teamIncentiveRatios?.[teamName] ||
    budgetData?.incentiveRatios ||
    { performance: 10, team: 70, member: 30 }
  );
};

// 소득세 계산 함수
export const calculateIncomeTax = (taxableIncome: number, familyCount: number): number => {
  // 과세표준 = 총급여 - 비과세항목들
  const baseAmount = taxableIncome;
  
  // 가족수에 따른 세금 감면 (예시)
  const familyDeduction = familyCount * 1500000 / 12; // 연 150만원 * 가족수 / 12개월
  const adjustedAmount = Math.max(0, baseAmount - familyDeduction);
  
  // 세금 테이블에서 해당 구간 찾기
  let tax = 0;
  for (const bracket of defaultTaxTable) {
    if (adjustedAmount >= bracket.min && adjustedAmount < bracket.max) {
      tax = bracket.baseTax + (adjustedAmount - bracket.min) * bracket.rate;
      break;
    }
  }
  
  return Math.round(tax);
};

// 팀별 인건비 총액 계산 (실장 인건비 배분 포함)
export const calculateTeamSalaryTotal = (employees: Employee[], teamName: string, allEmployees?: Employee[]): number => {
  const allEmps = allEmployees || employees;
  const director = allEmps.find(emp => emp.position === '실장');
  const hasAllocation = director && director.teamCostAllocation &&
    Object.values(director.teamCostAllocation as Record<string, number>)
      .reduce((s, v) => s + (Number(v) || 0), 0) > 0;

  // teamCostAllocation이 설정된 경우: 실장을 baseSalary에서 제외 (directorAllocation으로 배분)
  // 미설정인 경우: 실장을 일반 직원처럼 baseSalary에 포함 (directorAllocation = 0)
  const teamEmployees = employees.filter(emp => {
    if (emp.team !== teamName) return false;
    if (hasAllocation && emp.position === '실장') return false;
    return true;
  });

  const baseSalary = teamEmployees.reduce((sum, emp) =>
    sum + emp.salary + (emp.retirement || 0) + (emp.meal || 0) +
    (emp.housing || 0) + (emp.overtime || 0) + (emp.individual || 0) +
    (emp.other || 0), 0);

  const directorAllocation = calculateDirectorCostAllocation(allEmps, teamName);

  return baseSalary + directorAllocation;
};

// 팀별 순수 목표매출 계산 (인건비율 기준)
export const calculateTeamPureTargetSales = (
  teamSalaryTotal: number, 
  personnelPercentage: number
): number => {
  if (personnelPercentage <= 0) return 0;
  return Math.round(teamSalaryTotal / (personnelPercentage / 100));
};

// 팀별 통계 계산
export const calculateTeamStatistics = (
  sortedTeams: string[],
  employees: Employee[],
  teamActualSales: Record<string, number>,
  teamIncentiveCalculation: Record<string, TeamIncentiveCalculation>
) => {
  const totalEmployees = sortedTeams.reduce((total, teamName) => {
    const teamEmployees = employees.filter(emp => emp.team === teamName);
    return total + teamEmployees.filter(emp => emp.position === '팀원').length;
  }, 0);

  const totalSalary = sortedTeams.reduce((total, teamName) => {
    const teamEmployees = employees.filter(emp => emp.team === teamName);
    // 🔧 공통 유틸리티 함수 사용으로 계산 로직 통합
    const teamSalaryTotal = calculateSalaryTotal(teamEmployees);
    return total + teamSalaryTotal;
  }, 0);

  const totalActualSalesValue = sortedTeams.reduce((total, teamName) => {
    return total + (teamActualSales[teamName] || 0);
  }, 0);

  const totalIncentiveCalculationSales = sortedTeams.reduce((total, teamName) => {
    return total + (teamIncentiveCalculation[teamName]?.incentiveCalculationSales || 0);
  }, 0);

  // 🚀 추가: 실시간 계산된 차감분 합계 (월급 변경 시 즉시 반영)
  const totalDeductionAmount = sortedTeams.reduce((total, teamName) => {
    return total + (teamIncentiveCalculation[teamName]?.deductionAmount || 0);
  }, 0);

  return {
    totalEmployees,
    totalSalary,
    totalActualSales: totalActualSalesValue,
    totalIncentiveCalculationSales,
    totalDeductionAmount
  };
};

// 평균 달성률 계산
export const calculateAverageAchievement = (
  sortedTeams: string[], 
  employees: Employee[], 
  budgetData: any, 
  selectedMonth: string
): string => {
  if (sortedTeams.length === 0) return '0.0%';
  
  let totalPercentage = 0;
  let validTeams = 0;
  
  sortedTeams.forEach(teamName => {
    const teamSalaryTotal = calculateTeamSalaryTotal(employees, teamName);
    
    // 순수 목표매출을 기준으로 인건비율 계산
    const teamPureTargetSales = budgetData.monthlyData?.[selectedMonth]?.teamPureTargetSales || budgetData.teamPureTargetSales || {};
    const pureTarget = teamPureTargetSales[teamName] || 0;

    if (pureTarget > 0) {
      const teamPercentage = (teamSalaryTotal / pureTarget) * 100;
      totalPercentage += teamPercentage;
      validTeams++;
    }
  });
  
  const avgPercentage = validTeams > 0 ? (totalPercentage / validTeams) : 0;
  return `평균 ${avgPercentage.toFixed(1)}%`;
};

// 인센티브 계산
export const calculateIncentive = (
  exceededSales: number,
  incentiveRatio: { performance: number; team: number; member: number },
  isTeamLeader: boolean
): number => {
  const performanceIncentive = exceededSales * (incentiveRatio.performance / 100);
  
  if (isTeamLeader) {
    return Math.round(performanceIncentive * (incentiveRatio.team / 100));
  } else {
    return Math.round(performanceIncentive * (incentiveRatio.member / 100));
  }
};

/**
 * 주어진 직원 배열의 인건비 총합을 계산합니다.
 * 각 직원 객체의 salary 및 관련 수당 필드가 없으면 0으로 간주합니다.
 */
export function calculateSalaryTotal(employees: Employee[]): number {
  return employees.reduce((sum, emp) => 
    sum 
      + (emp.salary || 0)
      + (emp.retirement || 0)
      + (emp.meal || 0)
      + (emp.housing || 0)
      + (emp.overtime || 0)
      + (emp.individual || 0)
      + (emp.other || 0), 0);
}

/**
 * 팀(또는 전체)의 인건비 총합과 원하는 인건비율(%)을 받아 
 * 해당 인건비율을 만족하는 순수 목표매출을 계산합니다.
 */
export function calculatePureTargetSales(totalSalary: number, personnelPercentage: number): number {
  return personnelPercentage > 0
    ? Math.round(totalSalary / (personnelPercentage / 100))
    : 0;
}

/** 파트(아르바이트) 직위 판정 — 급여 규칙이 정규 직원과 다른 지점마다 이 하나를 쓴다. */
export const isPartPosition = (emp: { position?: string } | null | undefined): boolean =>
  emp?.position === '파트';

/**
 * 🔒 파트 직위 잠금 필드 — 파트는 개별수당으로만 급여를 지급한다(정책 결정 2026-08-04).
 * 파트 행에서 입력이 비활성화되고(EmployeeTableSection), 파트 전환 시 clearPartPayFields로 0 정리된다.
 * 월급·기타처럼 다음 달로 이월되는 고정 필드에 값이 잠긴 채 남아 매달 누적되는 것을 차단한다.
 *
 * ※ 개별수당(individual)은 여기 없다 — 파트 급여의 정본이자 유일하게 열린 입력이다.
 *   화면 잠금(EmployeeTableSection의 lockField)은 반드시 **이 목록에서 파생**해야 한다.
 *   "파트면 전부 잠금"으로 판단했다가 개별수당까지 잠겨, 2026-08-04부터 아르바이트 비용을
 *   한 푼도 입력할 수 없었다(8월 0원). 목록과 화면이 갈라지면 같은 결함이 재발한다.
 */
export const PART_LOCKED_FIELDS = [
  'salary',
  'nonTaxable',
  'meal',
  'housing',
  'overtimeMinutes',
  'overtime',
  'other',
  'familyCount',
] as const;

/**
 * 파트 전환 시 고정 급여 필드를 0으로 비우고 퇴직금을 0으로 고정한다.
 *
 * 퇴직금 0 (정책 결정 2026-09-04): 퇴직급여보장법상 지급 의무는 계속근로 1년 이상 +
 * 4주 평균 주 15시간 이상일 때 생기며 단발성 아르바이트는 해당하지 않는다. 코드도 이미
 * 파트를 정규 인건비 체계 밖에 둔다(인센티브 배분 제외·5인 이상 판정 제외).
 * 퇴직금은 그 자체로 다시 팀 인건비 총액에 들어가 목표매출을 올리므로, 붙이면 아르바이트
 * 비용의 8.3%만큼 목표가 부풀고 인센티브가 과소 계산된다.
 *
 * ⚠️ 같은 규칙이 updateAllowance(usePayrollBusinessLogic)에도 있어야 한다. 그쪽은 수당을
 *   입력할 때마다 퇴직금을 다시 계산하므로, 한쪽만 고치면 개별수당을 넣는 순간 되살아난다.
 *
 * 세금·실지급액은 호출부가 calculateDeductions로 재계산한다(공제 규칙 단일 정본 유지).
 */
export function clearPartPayFields<T extends Record<string, any>>(emp: T): T {
  const cleared: any = { ...emp, position: '파트' };
  PART_LOCKED_FIELDS.forEach(field => { cleared[field] = 0; });
  cleared.retirement = resolveRetirementPay(cleared);
  return cleared as T;
}

/**
 * 팀의 지난달 미달성 이월 금액(원)을 읽는다 — 최종목표 파생의 유일한 이월 정본.
 *
 * 최종목표 = 순수목표 + 이월금액. 이월금액은 조정을 '적용'하는 순간 동결되어 여기 저장되고,
 * 그 뒤 직원·급여가 바뀌어 순수목표가 움직여도 이월금액은 그대로다(목표는 따라 움직인다).
 * 해제하면 0이 기록된다. 이 값 하나로 "지금 목표에 이월이 얼마 들어 있나"가 결정되므로
 * 저장값을 보존해야 할지 다시 계산해야 할지 고민할 일이 없다 — 항상 다시 계산한다.
 *
 * 맵이 없거나 팀 키가 없으면 0이다. 추론하지 않는다.
 * (처음 설계는 "저장 목표 − 순수목표"의 양수 간격을 이월로 추론했는데, 그 간격이 진짜 이월인지
 *  저장 잔재인지 구분할 수 없어 잔재를 이월로 동결할 위험이 있었다 — Codex 재심 P1-5.
 *  운영 DB는 이관으로 전 월에 명시 기록되고, 새 달은 기본 월 객체·저장 경로가 {}를 기록한다.
 *  ⚠️ 그래서 이관 전에 배포하면 이월이 있는 달을 저장할 때 이월이 0으로 영속된다 — 순서 엄수.)
 */
export function getTeamAdjustmentCarryover(monthData: any, teamName: string): number {
  const map = monthData?.teamAdjustmentCarryover;
  if (!map || typeof map !== 'object') return 0;
  const v = Number(map[teamName]);
  return Number.isFinite(v) && v > 0 ? v : 0;
}

/**
 * 최종 목표매출 정본: 순수목표 + 저장된 이월금액. 예외 없음.
 *
 * 이전 설계는 adjustmentApplied 플래그가 참이면 저장된 teamTargetSales를 통째로 돌려주고
 * 아니면 순수+지난달미달성을 그 자리에서 계산했다. 플래그가 현실과 어긋나 있어(운영 DB에서
 * 실제 이월 6건 모두 false) 어느 쪽을 택해도 한쪽을 잃었고, 그 위에 Math.max 보존이 덧붙어
 * 목표가 내려가지 못했다. 이제 이 함수는 조정을 '적용'하지 않는다 — 적용은 명시적 행동이
 * 이월금액을 기록하는 것이고, 여기서는 기록된 값을 읽어 더할 뿐이다.
 */
export function resolveFinalTargetSales(
  budgetData: any,
  month: string,
  teamName: string,
  pureTarget: number
): number {
  // Firestore에 문자열 숫자가 들어 있으면 "38702737" + 38234505 = "3870273738234505" 가 된다
  // (Codex 재심 P1-7). 정본은 입력을 유한한 0 이상 숫자로 정규화한 뒤 더한다.
  const pure = Number(pureTarget);
  const safePure = Number.isFinite(pure) && pure > 0 ? pure : 0;
  const carryover = getTeamAdjustmentCarryover(budgetData?.monthlyData?.[month], teamName);
  return safePure + carryover;
}

/**
 * 팀 인건비 총합과 순수 목표매출을 받아 인건비율(%)을 계산합니다.
 * 순수 목표매출이 0이면 0을 반환합니다.
 */
export function calculatePersonnelPercentage(totalSalary: number, pureTargetSales: number): number {
  return pureTargetSales > 0
    ? (totalSalary / pureTargetSales) * 100
    : 0;
}

/**
 * 실장 인건비의 팀별 배분액을 계산합니다.
 * 실장의 인건비(인센티브 제외, 세전) × 해당 팀 배분 비율(%)
 */
export function calculateDirectorCostAllocation(
  allEmployees: Employee[],
  teamName: string
): number {
  const director = allEmployees.find(emp => emp.position === '실장' && emp.teamCostAllocation);
  if (!director || !director.teamCostAllocation) return 0;

  const allocationRate = director.teamCostAllocation[teamName];
  if (!allocationRate) return 0;

  // 실장 인건비 (인센티브 제외, 세전): salary + retirement + meal + housing + overtime + individual + other
  const directorCost =
    (director.salary || 0) +
    (director.retirement || 0) +
    (director.meal || 0) +
    (director.housing || 0) +
    (director.overtime || 0) +
    (director.individual || 0) +
    (director.other || 0);

  return Math.round(directorCost * (allocationRate / 100));
}

// ⏰ 5인 이상 사업장 판정 기준 인원
export const OVERTIME_WORKPLACE_MIN_EMPLOYEES = 5;

/**
 * 초과수당 150% 가산(5인 이상 사업장) 판정용 직원 수.
 * '파트' 직위는 제외하고, 급여가 있는 재직자만 센다.
 */
export const countOvertimeEligibleEmployees = (employees: Employee[]): number =>
  employees.filter(emp =>
    emp.position !== '파트' &&
    (emp.salary || 0) > 0 &&
    emp.status !== 'inactive'
  ).length;

/**
 * 단일 직원 초과수당 계산 — 통상임금(월급 ÷ 209시간) × 연장시간 × 가산율(1.5|1.0).
 * 초과수당 공식의 유일한 정의: 개별 편집(updateOvertimeMinutes)과 토글 재계산(recalcOvertimePay) 모두 이 함수를 쓴다.
 */
export const calculateOvertimePay = (salary: number, overtimeMinutes: number, applyMultiplier: boolean): number =>
  Math.round((salary / 209) * (overtimeMinutes / 60) * (applyMultiplier ? 1.5 : 1.0));

/**
 * 퇴직금 적립액 계산 — (세전 급여 + 초과수당 + 개별수당) ÷ 12.
 * 인센티브는 제외한다: 포함 시 목표매출→인센티브→퇴직금→인건비→목표매출 순환 참조 발생.
 * 퇴직금 산식의 유일한 정의: 급여/수당/분 편집·토글 재계산·저장 정규화 모두 이 함수를 쓴다.
 */
export const calculateRetirementPay = (salary: number, overtime: number, individual: number): number =>
  Math.round(((salary || 0) + (overtime || 0) + (individual || 0)) / 12);

/**
 * 직원 한 명의 퇴직금 — 로스터를 다루는 모든 경로(편집·토글 재계산·저장 정규화·불러오기)가
 * 반드시 이 함수를 쓴다. calculateRetirementPay 를 직접 부르면 파트 예외가 빠진다.
 *
 * 파트(아르바이트)는 0 (정책 결정 2026-09-04). 실제로 겪은 결함: clearPartPayFields 와
 * updateAllowance 두 곳만 고쳤더니 저장 경로(internalSaveAllData·saveSpecificMonthData)가
 * 정본 산식을 다시 돌려 퇴직금 30,473원이 되살아났고, 8월 목표가 145,110원 부풀었다.
 * 예외를 산식 옆에 두어 "어느 경로가 빠졌나"를 다시 따질 일이 없게 한다.
 */
export const resolveRetirementPay = (emp: {
  position?: string; salary?: number; overtime?: number; individual?: number;
}): number =>
  isPartPosition(emp) ? 0 : calculateRetirementPay(emp.salary || 0, emp.overtime || 0, emp.individual || 0);

/**
 * 저장 정규화 정본 — 로스터를 영속하기 전에 파생값을 정본 규칙으로 맞춘다.
 * 값이 이미 맞으면 **같은 객체를 그대로 돌려준다**(호출부의 '변경분만 setState' 최적화 유지).
 *
 *   ① 퇴직금  — resolveRetirementPay (파트 0)
 *   ② 공제    — 과세대상소득이 0 이하인데 세금이 남아 있으면 0으로 정리
 *
 * ②의 근거: 급여 0원 행에 4대보험 최소하한(국민연금 390,000·건강보험 370,000)이 적용돼
 * 세금 32,350원·실지급액 −32,350원이 붙던 결함이 있었다. 산식은 9821be0(2026-07-03)에서
 * 고쳤지만 **이미 저장된 값은 아무도 건드리지 않아** 2025-11부터 21개월 중 9개월 15행에
 * 그대로 남아 매달 불러오기로 이월됐다. 세금은 월급·비과세·가족수 편집과 직위 전환에서만
 * 재계산되는데, 파트 행은 그 세 칸이 잠겨 있어 사람이 고칠 방법조차 없었다.
 * 저장할 때마다 정리하면 같은 잔재가 다시 쌓이지 않는다.
 *
 * ② 는 '과세소득 0 이하'라는 좁은 조건에서만 발화한다 — 세금 전체를 다시 계산하지 않으므로
 * 다른 직원의 저장된 공제액을 건드리지 않는다.
 */
export function normalizeEmployeeForSave<T extends Record<string, any>>(emp: T): T {
  const retirement = resolveRetirementPay(emp);
  const withRetirement: T = retirement === (emp.retirement || 0) ? emp : { ...emp, retirement };

  const taxableIncome = (withRetirement.salary || 0) - (withRetirement.nonTaxable || 0);
  if (taxableIncome > 0) return withRetirement;
  if ((withRetirement.tax || 0) === 0 && (withRetirement.netSalary || 0) === (withRetirement.salary || 0)) {
    return withRetirement;
  }
  return { ...withRetirement, tax: 0, netSalary: withRetirement.salary || 0 };
}

/**
 * 연장근로시간(분) 기반 초과수당 재계산 (150% 가산 토글 공통 로직).
 * overtimeMinutes가 없는 직원(초과수당 직접 입력 포함)은 건드리지 않는다.
 * 초과수당은 퇴직금 산식의 입력이므로 퇴직금도 동반 갱신한다(어긋난 채 저장되는 것 방지).
 */
export const recalcOvertimePay = (employees: Employee[], applyMultiplier: boolean): Employee[] =>
  employees.map(emp => {
    if (emp.overtimeMinutes && emp.overtimeMinutes > 0) {
      const overtime = calculateOvertimePay(emp.salary, emp.overtimeMinutes, applyMultiplier);
      return { ...emp, overtime, retirement: resolveRetirementPay({ ...emp, overtime }) };
    }
    return emp;
  });